import { useEffect, useMemo, useState } from 'react';

function computeDeviceType() {
    if (typeof window === 'undefined') return 'desktop';

    const width = window.innerWidth || 0;
    const mm = typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : null;

    const isCoarsePointer = mm ? mm('(pointer: coarse)').matches : false;

    // Heuristic:
    // - small viewports are treated as phone
    // - coarse pointer + medium viewport also treated as phone/tablet-style UX
    if (width <= 768) return 'phone';
    if (isCoarsePointer && width <= 1024) return 'phone';
    return 'desktop';
}

export default function useDeviceType() {
    const [deviceType, setDeviceType] = useState(() => computeDeviceType());

    useEffect(() => {
        function update() {
            setDeviceType(computeDeviceType());
        }

        window.addEventListener('resize', update);
        window.addEventListener('orientationchange', update);

        // Keep in sync with pointer/viewport media changes as well.
        const mqls = [];
        if (typeof window.matchMedia === 'function') {
            mqls.push(window.matchMedia('(pointer: coarse)'));
            mqls.push(window.matchMedia('(max-width: 768px)'));
            mqls.push(window.matchMedia('(max-width: 1024px)'));
        }

        mqls.forEach((mql) => {
            try {
                mql.addEventListener('change', update);
            } catch {
                // Safari/older browsers
                mql.addListener(update);
            }
        });

        // Initial sync
        update();

        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('orientationchange', update);
            mqls.forEach((mql) => {
                try {
                    mql.removeEventListener('change', update);
                } catch {
                    mql.removeListener(update);
                }
            });
        };
    }, []);

    const flags = useMemo(() => {
        return {
            deviceType,
            isPhone: deviceType === 'phone',
            isDesktop: deviceType === 'desktop'
        };
    }, [deviceType]);

    return flags;
}
